"use client";

import { useUser } from "@clerk/react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { WatchlistLimitDialog } from "~/feature/watchlist/components/WatchlistLimitDialog";
import { useGuestWatchlistStorage } from "~/feature/watchlist/hooks/useGuestWatchlistStorage";
import { useWatchlistPermissions } from "~/feature/watchlist/hooks/useWatchlistPermissions";
import { InitWatchlist } from "~/feature/watchlist/providers/InitWatchlist";
import { GUEST_WATCHLIST_LIMIT } from "~/feature/watchlist/utils/constants";
import {
  useAddEventToUserWatchlistMutation,
  useDeleteEventFromUserWatchlistMutation,
} from "~/feature/watchlist/watchlistApi";
import type { IWatchlistMetadata } from "~/types/watchlist";
import { handleError } from "~/utils/handleError";

interface IWatchlistProviderProps {
  children: ReactNode;
}

interface IWatchlistContextType {
  watchlistIds: string[];
  watchlistLimit: number | null;
  isLoading: boolean;
  toggleWatchlist: (groupId: string) => Promise<void>;
  isEventAlreadyInWatchlist: (groupId: string) => boolean;
  setUserWatchlist: (ids: string[], metadata: IWatchlistMetadata) => void;
  setLoading: (value: boolean) => void;
}

const WatchlistContext = createContext<IWatchlistContextType | null>(null);

export const WatchlistProvider = ({ children }: IWatchlistProviderProps) => {
  const { isSignedIn, isLoaded } = useUser();
  const { hasNoLimits } = useWatchlistPermissions();

  const {
    ids: guestIds,
    setIds: setGuestIds,
    clear: clearGuestStorage,
  } = useGuestWatchlistStorage();

  const [userIds, setUserIds] = useState<string[]>([]);
  const [userMetadata, setUserMetadata] = useState<IWatchlistMetadata | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);

  const [addEventToUserWatchlist] = useAddEventToUserWatchlistMutation();
  const [deleteEventFromUserWatchlist] =
    useDeleteEventFromUserWatchlistMutation();

  const watchlistIds = isSignedIn ? userIds : guestIds;

  const watchlistLimit = useMemo<number | null>(() => {
    if (!isSignedIn) {
      return GUEST_WATCHLIST_LIMIT;
    }
    if (hasNoLimits) {
      return null;
    }
    return userMetadata?.maxLimit ?? null;
  }, [isSignedIn, hasNoLimits, userMetadata?.maxLimit]);

  const isWatchlistFull = useMemo(() => {
    if (watchlistLimit === null) {
      return false;
    }
    return watchlistIds.length >= watchlistLimit;
  }, [watchlistIds.length, watchlistLimit]);

  const isEventAlreadyInWatchlist = useCallback(
    (groupId: string) => watchlistIds.includes(groupId),
    [watchlistIds]
  );

  const setUserWatchlist = useCallback(
    (ids: string[], metadata: IWatchlistMetadata) => {
      setUserIds(ids);
      setUserMetadata(metadata);
    },
    []
  );

  const setLoading = useCallback((value: boolean) => setIsLoading(value), []);

  const addEventAsGuest = useCallback(
    (eventId: string) => {
      setGuestIds([...guestIds, eventId]);
    },
    [guestIds, setGuestIds]
  );

  const removeEventAsGuest = useCallback(
    (eventId: string) => {
      setGuestIds(guestIds.filter((id) => id !== eventId));
    },
    [guestIds, setGuestIds]
  );

  const addEventAsUser = useCallback(
    async (eventId: string) => {
      try {
        const response = await addEventToUserWatchlist({
          id: eventId,
        }).unwrap();
        setUserWatchlist(response.ids, response.metadata);
      } catch (error) {
        handleError(error);
      }
    },
    [addEventToUserWatchlist, setUserWatchlist]
  );

  const removeEventAsUser = useCallback(
    async (eventId: string) => {
      try {
        const response = await deleteEventFromUserWatchlist({
          id: eventId,
        }).unwrap();
        setUserWatchlist(response.ids, response.metadata);
      } catch (error) {
        handleError(error);
      }
    },
    [deleteEventFromUserWatchlist, setUserWatchlist]
  );

  const toggleWatchlist = useCallback(
    async (eventId: string) => {
      if (!isLoaded) {
        return;
      }

      const isInWatchlist = isEventAlreadyInWatchlist(eventId);

      if (isInWatchlist) {
        if (isSignedIn) {
          await removeEventAsUser(eventId);
        } else {
          removeEventAsGuest(eventId);
        }
        return;
      }

      if (isWatchlistFull) {
        setLimitDialogOpen(true);
        return;
      }

      if (isSignedIn) {
        await addEventAsUser(eventId);
      } else {
        addEventAsGuest(eventId);
      }
    },
    [
      addEventAsGuest,
      addEventAsUser,
      isEventAlreadyInWatchlist,
      isLoaded,
      isSignedIn,
      isWatchlistFull,
      removeEventAsGuest,
      removeEventAsUser,
    ]
  );

  const closeLimitDialog = useCallback(() => setLimitDialogOpen(false), []);

  const value = useMemo(
    () => ({
      watchlistIds,
      watchlistLimit,
      isLoading,
      toggleWatchlist,
      isEventAlreadyInWatchlist,
      setUserWatchlist,
      setLoading,
    }),
    [
      watchlistIds,
      watchlistLimit,
      isLoading,
      toggleWatchlist,
      isEventAlreadyInWatchlist,
      setUserWatchlist,
      setLoading,
    ]
  );

  return (
    <WatchlistContext.Provider value={value}>
      <InitWatchlist
        userMetadata={userMetadata}
        clearGuestStorage={clearGuestStorage}
      />
      <WatchlistLimitDialog open={limitDialogOpen} onClose={closeLimitDialog} />
      {children}
    </WatchlistContext.Provider>
  );
};

export const useWatchlist = (): IWatchlistContextType => {
  const context = useContext(WatchlistContext);

  if (!context) {
    throw new Error("useWatchlist must be used within a WatchlistProvider");
  }

  return context;
};
